/* eslint-disable react/jsx-no-bind */
//@flow
import React, { useRef, useEffect, useState, forwardRef } from 'react';
import { Flex } from '@locus-taxy/neo-ui';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { noop } from 'lodash';
import { type TFunction } from 'react-i18next';
import domtoimage from 'dom-to-image';

import { dialogFactory } from 'common/components/core/dialog/';
import { Button, ButtonWithPopover } from 'common/components/core/button/';
import { PopoverTriggerEnum } from 'common/components/core/popover/popOverFactory';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import theme from './QrCodeModal.scss';

import type { QrCodesType } from 'common/entity/generic/MasterContainer';

const base64PrefixString = 'data:image/png;base64,';

const getFormattedBase64String = base64 => `${base64PrefixString}${base64}`;

const handleDownload = (qrCodeUrl, riderId) => {
    const anchor = document.createElement('a');
    anchor.setAttribute('href', qrCodeUrl);
    anchor.setAttribute('download', riderId);
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
};

const handleDownloadAll = qrCodes => () => {
    qrCodes.forEach(eachQrCodeObj => {
        const { img, riderId } = eachQrCodeObj;
        handleDownload(img, riderId);
    });
};

const QrCodeCardDownloadTemplate = forwardRef(
    ({ qrCodeImage, riderId, validity, t }, ref) => (
        <div style={{ opacity: 0 }}>
            <Flex
                sx={{
                    width: 500,
                    height: 600,
                    backgroundColor: '#fff',
                    position: 'fixed',
                    fontSize: 24,
                    flexWrap: 'wrap',
                    top: 0,
                    left: 0
                }}
                ref={ref}
            >
                <Flex sx={{ borderBottom: '1px solid #e7e9ee', width: '100%' }}>
                    <img
                        className={theme.qrCode}
                        src={qrCodeImage}
                        style={{ width: '100%', height: '500px' }}
                    />
                </Flex>
                <Flex
                    sx={{
                        padding: '10px',
                        width: '100%',
                        flexWrap: 'wrap',
                        backgroundColor: '#fff'
                    }}
                >
                    <Flex
                        width={'100%'}
                        justifyContent="center"
                        sx={{ fontWeight: 600 }}
                    >
                        {riderId}
                    </Flex>
                    <Flex width={'100%'} justifyContent="center">
                        {/*TODO: not sure when we show this. need to check and update*/}
                        {`${t(
                            'entityManagement:validTill'
                        )} : ${TimeUtils.format(
                            validity,
                            DateTimeFormat.SHEET_DATE_FORMAT
                        )}, ${TimeUtils.format(
                            validity,
                            DateTimeFormat.FANCY_TIME_FORMAT
                        )}`}
                    </Flex>
                </Flex>
            </Flex>
        </div>
    )
);

type QrCodeCardPropTypes = {
    t: TFunction,
    qrCodeImage: string,
    validity: string,
    isOnly: boolean,
    riderId: string,
    onTemplateReady: () => void
};

const QrCodeCard = ({
    qrCodeImage,
    riderId,
    validity,
    t,
    isOnly,
    onTemplateReady
}: QrCodeCardPropTypes) => {
    const qrCodeTemplateRef = useRef(null);
    const [qrCodeCardImage, setQrCodeCardImage] = useState(null);
    useEffect(() => {
        const geQrCodeCardImage = async qrCodeTemplateRef => {
            const dataUrl = await domtoimage.toPng(qrCodeTemplateRef.current, {
                transform: 'scale(0.5)'
            });
            setQrCodeCardImage(dataUrl);
            onTemplateReady(dataUrl, riderId);
        };
        if (qrCodeTemplateRef) {
            geQrCodeCardImage(qrCodeTemplateRef);
        }
    }, [qrCodeTemplateRef]);

    return (
        <div className={theme.qrCodeCard}>
            <div className={theme.qrCodeWrapper}>
                <img className={theme.qrCode} src={qrCodeImage} />
            </div>
            <div className={theme.bottomPart}>
                <div className={theme.infoWrapper}>
                    <div className={theme.riderId}>{riderId}</div>
                    <div className={theme.validTill}>{`${t(
                        'entityManagement:validTill'
                    )} : ${TimeUtils.format(
                        validity,
                        DateTimeFormat.SHEET_DATE_FORMAT
                    )}, ${TimeUtils.format(
                        validity,
                        DateTimeFormat.FANCY_TIME_FORMAT
                    )}`}</div>
                </div>
                {!isOnly && (
                    <a
                        className={theme.download}
                        href={qrCodeCardImage}
                        download={riderId}
                    >
                        <ButtonWithPopover
                            onClick={noop}
                            theme={theme}
                            icon={'download'}
                            popOverContent={t(
                                'entityManagement:downloadThisQrCode'
                            )}
                            triggerPopoverOn={PopoverTriggerEnum.Hover}
                        />
                    </a>
                )}
            </div>
            <QrCodeCardDownloadTemplate
                ref={qrCodeTemplateRef}
                t={t}
                riderId={riderId}
                qrCodeImage={qrCodeImage}
                validity={validity}
            />
        </div>
    );
};

type QrCodeInnerContentPropTypes = {
    t: TFunction,
    qrCodes: QrCodesType
};

const QrCodeInnerContent = ({ t, qrCodes }: QrCodeInnerContentPropTypes) => {
    const [qrCodeTemplates, setQrCodeTemplates] = useState({});
    const numberOfQrCodes = qrCodes.length;
    const handleTemplateReady = (dataUrl, riderId) => {
        setQrCodeTemplates(state => ({
            ...state,
            [riderId]: {
                img: dataUrl,
                riderId
            }
        }));
    };

    return (
        <div className={theme.qrCodesInnerContent}>
            <div className={theme.qrCodesWrapper}>
                <div className={theme.qrCodes}>
                    {qrCodes.map(
                        ({ qrCodeImage, validity, riderId: { riderId } }) => (
                            <QrCodeCard
                                key={riderId}
                                t={t}
                                qrCodeImage={getFormattedBase64String(
                                    qrCodeImage
                                )}
                                validity={validity}
                                riderId={riderId}
                                isOnly={numberOfQrCodes === 1}
                                onTemplateReady={handleTemplateReady}
                            />
                        )
                    )}
                </div>
            </div>
            <div className={theme.downloadAllWrapper}>
                <div className={theme.downloadAll}>
                    <Button
                        label={t(
                            `entityManagement:download${
                                numberOfQrCodes === 1 ? '' : 'All'
                            }`
                        )}
                        onClick={handleDownloadAll(
                            Object.values(qrCodeTemplates)
                        )}
                        theme={theme}
                        icon="download"
                    />
                </div>
            </div>
        </div>
    );
};

const QrCodeModalWithFactory = dialogFactory({
    InnerContent: QrCodeInnerContent
});

type QrCodeModalProps = {
    qrCodes: QrCodesType,
    t: TFunction
};

const QrCodeModal = (props: QrCodeModalProps) => {
    const { qrCodes } = props;
    const numberOfQrCodes = qrCodes.length;
    return (
        <QrCodeModalWithFactory
            {...props}
            className={classnames(props.className, theme.qrCodeModalOverlay, {
                [theme.qrCode1gridOverlay]: numberOfQrCodes === 1,
                [theme.qrCode2gridOverlay]:
                    numberOfQrCodes > 1 && numberOfQrCodes < 5
            })}
        />
    );
};

QrCodeModal.defaultProps = {
    qrCodes: []
};

export default themr('QrCodeModal', theme)(QrCodeModal);
